/**
 * Resolve bank / pay-in image URLs for the API that actually serves /Images/*.
 *
 * DB may store relative or absolute URLs. Frontend host https://fxbazzar.com (443)
 * does NOT serve UploadBankImages (404). API static files are on :2025.
 */
import axios from "axios";

const UPLOAD_API_ORIGIN = "https://fxbazzar.com:2025";

function stripApiSuffix(u) {
  return String(u || "")
    .trim()
    .replace(/\/api\/v1\/?$/i, "")
    .replace(/\/+$/, "");
}

/**
 * fxbazzar.com / www.fxbazzar.com (no API port) → force :2025 where bank images are served.
 * Leave localhost / other hosts unchanged.
 */
export function normalizeUploadApiOrigin(origin) {
  const o = stripApiSuffix(origin);
  if (!o) return UPLOAD_API_ORIGIN;
  try {
    const u = new URL(o.includes("://") ? o : `https://${o}`);
    if (/^(?:www\.)?fxbazzar\.com$/i.test(u.hostname)) {
      // Website :443 does not host UploadBankImages; API does on 2025.
      if (!u.port || u.port === "80" || u.port === "443") {
        return UPLOAD_API_ORIGIN;
      }
    }
    return `${u.protocol}//${u.host}`.replace(/\/+$/, "");
  } catch {
    return o;
  }
}

/** Origin that can serve /Images/* for the current session. */
export function resolveUploadApiOrigin() {
  const fromEnv = stripApiSuffix(process.env.REACT_APP_UPLOAD_ORIGIN || "");
  if (fromEnv) return normalizeUploadApiOrigin(fromEnv);

  // Local npm start: files live on this PC; CRA proxies /Images (setupProxy.js).
  // Do this before axios/Firebase baseURL, which is often https://fxbazzar.com/api/v1 (website :443 → 404).
  if (typeof window !== "undefined" && process.env.NODE_ENV === "development") {
    return window.location.origin;
  }

  const axiosBase = stripApiSuffix(axios.defaults?.baseURL || "");
  if (/^https?:\/\//i.test(axiosBase)) return normalizeUploadApiOrigin(axiosBase);

  const apiUrl = stripApiSuffix(process.env.REACT_APP_API_URL || "");
  if (/^https?:\/\//i.test(apiUrl)) return normalizeUploadApiOrigin(apiUrl);

  if (typeof window !== "undefined") {
    return normalizeUploadApiOrigin(window.location.origin);
  }

  return UPLOAD_API_ORIGIN;
}

/** Extract /Images/UploadBankImages|UploadPayInImages/... path from any stored value. */
function extractUploadPath(src) {
  const m = String(src || "").match(
    /(\/Images\/(?:UploadBankImages|UploadPayInImages)\/[^?#]+)/i
  );
  return m ? m[1] : "";
}

export function resolveUploadImageUrl(raw) {
  const src = String(raw || "").trim();
  if (!src) return "";
  if (src.startsWith("data:")) return src;

  const apiOrigin = resolveUploadApiOrigin();
  const path = extractUploadPath(src);

  if (path) {
    return `${apiOrigin}${path}`;
  }

  if (/^https?:\/\//i.test(src)) {
    // Unknown absolute URL — still avoid bare fxbazzar.com:443 for upload paths.
    try {
      const u = new URL(src);
      if (/UploadBankImages|UploadPayInImages/i.test(u.pathname)) {
        return `${normalizeUploadApiOrigin(u.origin)}${u.pathname}`;
      }
    } catch {
      /* keep */
    }
    return src;
  }

  if (src.startsWith("/")) return `${apiOrigin}${src}`;

  return `${apiOrigin}/Images/UploadBankImages/${src.replace(/^\/+/, "")}`;
}
